import React, { useContext, useCallback, useEffect, createContext, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ReplaySubject, Subject } from 'rxjs';

import { rateRefreshSubject } from './rate-refresh-subject';
import { map } from 'rxjs/operators';

import { noop } from 'ui-components/default-props/noop';

import { bulkLoadFulfillmentPlanIds } from '../store/bulkload/bulkload.api';

import * as Actions from '../store/csw/csw.actions';

import { useSmartFillData, useSmartFillMetaData } from '../hooks/smartfill/use-smartfill-data';
import { useIsSmartFillEnabledForSeller } from '../hooks/smartfill/use-highlight-smartfill-field';
import { usePackagesWeightInSellerUnit } from '../hooks/use-packages-weight-in-seller-unit';
import { convertDimensions } from '../services/unit-services/dimensions/dimension-convert-service';

import Global from '../store/global-selectors';
import { createArrayValue, useSaveUserSettingPrepayDutyAlert } from './context/hooks';
import { setupExternalShipFromIdForContext } from './context/external-ship-from-setup';
import { useConfigurePackages } from './hooks/use-configure-packages';
import { usePackagesState } from './hooks/use-packages-state';
import { useShipFromState } from './hooks/use-ship-from-state';
import { useInsuranceProviderState } from './hooks/use-insurance-provider-state';
import { useShippingServiceState } from './hooks/use-shipping-service-state';
import { useServiceCollection } from './hooks/use-service-collection';
import { useExternalFulfillmentServiceState } from './hooks/use-external-fulfillment-service-state';
import { useOtherShippingOptionsState } from './hooks/use-other-shipping-options-state';
import { useApplyPreset } from './hooks/use-apply-preset';
import { useCustomsState } from './hooks/use-customs-state';
import { useDriverTipState } from './hooks/use-driver-tip-state';
import { usePickupTimeState } from './hooks/use-pickup-time-state';
import { useShipDateState } from './hooks/use-ship-date-state';
import { addressPropType } from '../address/address-fields/address-prop-type';
import { CurrencyPropType } from '../prop-types/currency';
import { InsuranceProviderPropType } from '../prop-types/insurance-provider';
import { ShippingOptionsPropType } from '../prop-types/shipping-options';
import { PackagePropType } from '../prop-types/package';
import { BestWayToShipPropType } from '../prop-types/best-way-to-ship';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { defaultCurrency } from '../default-props/default-currency';
import { InsuranceProviders } from '../enums/insurance-providers';
import { useUpdateInsurancePreference } from './insurance-promo/use-update-insurance-preference';
import { useVisibleRateErrors } from '../orders/order-details/freight-details/use-visible-rate-errors';
import { useVisibleRateWarnings } from '../orders/order-details/freight-details/use-visible-rate-warnings';

/** @typedef {import('./context-types').FulfillmentPlannerContextValue} FulfillmentPlannerContextValue */

/** @type {React.Context<FulfillmentPlannerContextValue>} */
const FulfillmentPlannerContext = createContext({
  availableSellerShippingServices: emptyArray,
  isDefaultValue: true,
  createLabelErrors: emptyArray,
  rateErrors: emptyArray,
  totalCost: undefined,
  warnings: emptyArray,
  shipDate: undefined,
  applyPresetConfiguration: noop,
  onShipDateChange: noop,
  serviceCollectionGuid: undefined,
  setHasSmartFillDataBeenChanged: noop,
  hasSmartFillDataBeenChanged: emptyObject,
  isSmartFillEnabled: false,
  insuranceProvider: InsuranceProviders.None,
  packages: emptyArray,
});

export const useFulfillmentPlannerContext = () => {
  return useContext(FulfillmentPlannerContext);
};

export { rateRefreshSubject };
export const createLabelSubject = new ReplaySubject(100, 30000);
export const availableSellerShippingServicesSubject = new ReplaySubject(100, 30000);
export const serviceCollectionSubject = new Subject();

const FulfillmentPlannerContextProvider = ({
  availableDutiesOptions = emptyArray,
  availableSellerShippingServices,
  bestWayToShip = emptyObject,
  canSyncWithEntities = false,
  children,
  cswTypeId,
  customs = emptyObject,
  customsReason = '',
  driverTip = defaultCurrency,
  emailAddress = null,
  fulfillmentPlanId,
  parentComponentName = '',
  estimatedDeliveryTime = null,
  fulfillmentServiceId = null,
  initialEstimatedPickup = null,
  initialRateError = emptyArray,
  insuranceProvider,
  isInternational = false,
  isReturn = false,
  items = emptyArray,
  needsCustoms = false,
  onAvailableSellerShippingServicesChange = noop,
  onConfigurationChange: onConfigurationChangeProp,
  onIsReturnChanged = noop,
  onShipToChange = noop,
  onShipToEmailChange = noop,
  otherShippingOptions = emptyObject,
  packages = emptyArray,
  pickup = null,
  rateTotalCost = null,
  restrictions = emptyObject,
  serviceCollectionGuid = null,
  serviceIdAndSellerLabelProviderId,
  shipDate = null,
  shipFromId = null,
  shipTo,
  shouldShowDutiesAndTaxes = false,
  shouldSyncOnBulkUpdate = false,
  triggerRate,
  updatedRateWarnings = emptyArray,
  isTriggerRateDisabled = false,
}) => {
  const [rateErrors, setRateErrors] = useState(initialRateError);
  const dispatch = useDispatch();
  const [isRating, setIsRating] = useState(false);
  const [deliveryTime, setDeliveryTime] = useState(estimatedDeliveryTime);
  const [totalCost, setTotalCost] = useState(rateTotalCost);

  const [warnings, setWarnings] = useState(updatedRateWarnings);

  const visibleRateErrors = useVisibleRateErrors(fulfillmentPlanId, rateErrors);
  const visibleWarnings = useVisibleRateWarnings(fulfillmentPlanId, warnings);

  const isSmartFillEnabled = useIsSmartFillEnabledForSeller();
  const {
    Weight: smartFillWeight,
    Height: smartFillHeight,
    Length: smartFillLength,
    Width: smartFillWidth,
    Package: smartFillPackage,
  } = useSmartFillData(fulfillmentPlanId);

  const smartFillMetaData = useSmartFillMetaData(fulfillmentPlanId);

  const weight = usePackagesWeightInSellerUnit(packages);
  const [firstPackage = {}] = packages;
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const dimensions = convertDimensions(firstPackage.dimensions, sellerDefaultDimensionUnit) || {};

  const [hasSmartFillDataBeenChanged, setHasSmartFillDataBeenChanged] = useState({
    weight: smartFillWeight == null || smartFillWeight !== weight.value,

    height: smartFillHeight == null || smartFillHeight !== dimensions.height,

    length: smartFillLength == null || smartFillLength !== dimensions.length,

    width: smartFillWidth == null || smartFillWidth !== dimensions.width,

    package: smartFillPackage == null || parseInt(smartFillPackage, 10) !== parseInt(firstPackage.packageTypeId, 10),
  });

  // label state
  const [isCreatingLabel, setIsCreatingLabel] = useState(false);
  const [createLabelErrors, setCreateLabelErrors] = useState([]);
  const [didLabelCreationSucceed, setDidLabelCreationSucceed] = useState(false);
  const [didLabelCreationFail, setDidLabelCreationFail] = useState(false);

  const [estimatedPickup, setEstimatedPickup] = useState(initialEstimatedPickup);

  const onConfigurationChange = useCallback(
    (...args) => {
      onConfigurationChangeProp(...args);
      setRateErrors([]);
      setWarnings(emptyArray);
    },
    [onConfigurationChangeProp],
  );

  const { onShipDateChange } = useShipDateState(fulfillmentPlanId, onConfigurationChange);

  const { externalShipFromId, fulfillmentType, onShipFromChange } = useShipFromState(fulfillmentPlanId, shipFromId, onConfigurationChange);

  const configurePackages = useConfigurePackages();
  const {
    checkPackagesAfterServiceChange,
    clearInsuranceValues,
    onApplyServiceMappingPackage,
    onContentDescriptionChange,
    onInsuredAmountChange,
    onMultiPackageSave,
    onPackageChange,
    onPackagesSave,
    onWeightAndDimsChange,
    updatePackagesFromRateBrowserConfigureLabel,
    initInsuranceValue,
    calculateRecommendedInsuranceValue,
    applyPackageSetFromList,
  } = usePackagesState(fulfillmentPlanId, packages, onConfigurationChange, sellerDefaultDimensionUnit);

  const { onInsuranceProviderChange, onPresetWithInsuranceProviderApplied } = useInsuranceProviderState(
    fulfillmentPlanId,
    insuranceProvider,
    clearInsuranceValues,
    initInsuranceValue,
    onConfigurationChange,
  );

  const { onConfirmationChange, onOtherShippingOptionsChange, onSelectedDutyOptionChange, onServiceChangeUpdateOtherShippingOptions } =
    useOtherShippingOptionsState(fulfillmentPlanId, otherShippingOptions, onConfigurationChange);

  const { onSellerLabelProviderChange, onServiceChange } = useShippingServiceState(
    fulfillmentPlanId,
    onConfirmationChange,
    onServiceChangeUpdateOtherShippingOptions,
    onConfigurationChange,
  );

  const {
    onServiceCollectionChange,
    onUpdatePackageServiceCollection,
    prePopulatePackage,
    prePopulateConfirmation,
    onUpdateConfirmationServiceCollection,
  } = useServiceCollection(fulfillmentPlanId, onConfigurationChange, otherShippingOptions);

  const { onFulfillmentServiceChange } = useExternalFulfillmentServiceState(fulfillmentPlanId, onConfigurationChange);

  const { onDriverTipChange } = useDriverTipState(fulfillmentPlanId, onConfigurationChange);

  const { onPickupChange } = usePickupTimeState(fulfillmentPlanId, onConfigurationChange);

  const { onCustomsChange } = useCustomsState(fulfillmentPlanId, onConfigurationChange);

  const onShipFromChangeWithSideEffects = useCallback(
    (newShipFromId) => {
      onShipFromChange(newShipFromId);
    },
    [onShipFromChange],
  );

  const onServiceChangeWithSideEffects = useCallback(
    ({ serviceId, sellerLabelProviderId, serviceCollectionGuid: newServiceCollectionGuid, sendAnalytics }) => {
      onServiceChange({
        serviceId: serviceId || null,
        sellerLabelProviderId: sellerLabelProviderId || null,
        serviceCollectionGuid: newServiceCollectionGuid || null,
        confirmationId: otherShippingOptions?.confirmationEnumId,
        sendAnalytics,
      });
      checkPackagesAfterServiceChange(serviceId, newServiceCollectionGuid);
    },
    [onServiceChange, checkPackagesAfterServiceChange, otherShippingOptions?.confirmationEnumId],
  );

  // Rating Refresh Subject
  useEffect(() => {
    const subscription = rateRefreshSubject
      .pipe(
        map((next) => {
          // a space for setting up defaults that cannot be done with destructuring
          const safeErrors = createArrayValue(next.errors);
          const rateObject = next.rateObject || {};
          rateObject.rateWarnings = createArrayValue(rateObject.rateWarnings);

          return {
            ...next,
            errors: safeErrors,
            rateObject,
          };
        }),
      )
      .subscribe(
        ({
          fulfillmentPlanId: ratingFulfillmentPlanId,
          isRefreshingRate,
          errors,
          rateObject: { totalCost: newTotalCost, estimatedDelivery, estimatedPickup: newEstimatedPickup, rateWarnings },
        }) => {
          if (ratingFulfillmentPlanId === fulfillmentPlanId) {
            setIsRating(isRefreshingRate);
            setRateErrors(errors);
            setWarnings(rateWarnings.length ? rateWarnings.map((warn) => warn.warning) : rateWarnings);
            setEstimatedPickup(newEstimatedPickup);
            if (estimatedDelivery || (!estimatedDelivery && errors.length)) {
              setDeliveryTime(estimatedDelivery);
            }

            let orderCost = newTotalCost;
            if (!orderCost && errors.length) {
              orderCost = undefined;
            }
            setTotalCost(orderCost);
          }
        },
      );
    return () => subscription.unsubscribe();
  }, [fulfillmentPlanId, setDeliveryTime, setRateErrors, setTotalCost, setWarnings]);

  // CreateLabel Subject
  useEffect(() => {
    const subscription = createLabelSubject.subscribe(
      ({
        fulfillmentPlanId: createLabelFulfillmentPlanId,
        isCreatingLabel: creatingLabel,
        didCreateExternalFulfillment = false,
        errorMessages,
        didLabelCreationComplete = false,
      }) => {
        if (createLabelFulfillmentPlanId === fulfillmentPlanId) {
          setDidLabelCreationSucceed(didCreateExternalFulfillment || didLabelCreationComplete);
          if (creatingLabel !== undefined) {
            setIsCreatingLabel(creatingLabel);
          }
          if (errorMessages) {
            setCreateLabelErrors(errorMessages);
            if (errorMessages.length > 0) {
              setDidLabelCreationFail(true);
              return;
            }
            setDidLabelCreationFail(false);
          }
        }
      },
    );
    return () => subscription.unsubscribe();
  }, [fulfillmentPlanId]);

  // AvailableSellerShippingServices Subject
  useEffect(() => {
    const subscription = availableSellerShippingServicesSubject.subscribe(
      ({ fulfillmentPlanId: servicesFulfillmentPlanId, shippingServices }) => {
        if (servicesFulfillmentPlanId === fulfillmentPlanId) {
          onAvailableSellerShippingServicesChange(shippingServices);
          if (
            serviceIdAndSellerLabelProviderId?.serviceId &&
            !shippingServices.includes(Number.parseInt(serviceIdAndSellerLabelProviderId.serviceId, 10))
          ) {
            onServiceChangeWithSideEffects({
              serviceId: null,
              sellerLabelProviderId: null,
            });
          }
        }
      },
    );
    return () => subscription.unsubscribe();
  }, [fulfillmentPlanId, onServiceChangeWithSideEffects, serviceIdAndSellerLabelProviderId, onAvailableSellerShippingServicesChange]);

  useEffect(() => {
    const subscription = serviceCollectionSubject.subscribe(
      ({
        fulfillmentPlanId: serviceCollectionFulfillmentPlanId,
        serviceCollectionGuid: newServiceCollectionGuid,
        serviceId: newServiceId,
        sellerLabelProviderId: newSellerLabelProviderId,
        confirmationId: newConfirmationId,
        packageTypeId: newPackageTypeId,
        packages: newPackages,
        carrierId,
      }) => {
        const shouldAllowFurtherUpdate = !!(serviceCollectionFulfillmentPlanId === fulfillmentPlanId && newServiceCollectionGuid);

        if (!shouldAllowFurtherUpdate) {
          return;
        }

        const shouldUpdateServiceCollection =
          serviceIdAndSellerLabelProviderId?.serviceId !== newServiceId ||
          serviceIdAndSellerLabelProviderId?.sellerLabelProviderId !== newSellerLabelProviderId;

        if (shouldUpdateServiceCollection) {
          onServiceCollectionChange({
            serviceId: newServiceId,
            sellerLabelProviderId: newSellerLabelProviderId,
            serviceCollectionGuid: newServiceCollectionGuid,
          });
        }

        const [currentPackage = {}] = packages;
        const shouldUpdatePackage = prePopulatePackage;

        if (shouldUpdatePackage) {
          onUpdatePackageServiceCollection({
            currentPackage,
            packageTypeId: newPackageTypeId,
            carrierId,
            newPackages,
          });
        }

        const shouldUpdateConfirmationId = prePopulateConfirmation && newConfirmationId !== otherShippingOptions.confirmationEnumId;

        if (shouldUpdateConfirmationId) {
          onUpdateConfirmationServiceCollection({
            confirmationId: newConfirmationId,
          });
        }
      },
    );

    return () => subscription.unsubscribe();
  }, [
    prePopulateConfirmation,
    prePopulatePackage,
    fulfillmentPlanId,
    onServiceCollectionChange,
    onUpdateConfirmationServiceCollection,
    onUpdatePackageServiceCollection,
    packages,
    serviceIdAndSellerLabelProviderId,
    otherShippingOptions,
  ]);

  const syncCswWithUpdatedEntity = useCallback(
    (fulfillmentPlan) => {
      const { labelConfiguration, international } = fulfillmentPlan;
      const updatedNeedsCustoms = !!international?.needsCustoms;
      const updatedCustoms = labelConfiguration?.customs;
      if (labelConfiguration?.packages) {
        const configuredPackages = configurePackages(labelConfiguration.packages);
        onPackagesSave(configuredPackages);
      }

      if (updatedCustoms) {
        onCustomsChange(updatedCustoms, updatedNeedsCustoms);
      } else {
        // reset customs
        onCustomsChange({ customsItems: [] }, updatedNeedsCustoms);
      }
    },
    [configurePackages, onCustomsChange, onPackagesSave],
  );

  const bulkUpdateFinishedHandler = useCallback(() => {
    // bulk update can cause these to update after the context mounts and is initialized
    // due to the async nature of the backend task
    if (!shouldSyncOnBulkUpdate) {
      return;
    }

    bulkLoadFulfillmentPlanIds([fulfillmentPlanId]).then(({ fulfillmentPlans }) => {
      const { labelConfiguration, externalFulfillmentConfiguration } = fulfillmentPlans[0];
      if (labelConfiguration?.shipFromId) {
        onShipFromChange(labelConfiguration.shipFromId);
      } else {
        const transformedExternalId = setupExternalShipFromIdForContext(externalFulfillmentConfiguration.sellerFulfillmentProviderId);
        onShipFromChange(transformedExternalId);
      }
      if (labelConfiguration?.packages) {
        const configuredPackages = configurePackages(labelConfiguration.packages);
        onPackagesSave(configuredPackages);
      }
      if (labelConfiguration?.insuranceProvider) {
        onInsuranceProviderChange(labelConfiguration.insuranceProvider);
      }
      if (labelConfiguration?.serviceId || labelConfiguration?.serviceCollectionGuid) {
        onServiceChange({
          serviceId: labelConfiguration.serviceId,
          sellerLabelProviderId: labelConfiguration.sellerLabelProviderId,
          serviceCollectionGuid: labelConfiguration.serviceCollectionGuid,
          confirmationId: labelConfiguration?.options?.confirmationEnumId?.toString(),
        });
      }
      if (externalFulfillmentConfiguration?.fulfillmentServiceId) {
        onFulfillmentServiceChange(externalFulfillmentConfiguration.fulfillmentServiceId);
      }
      if (labelConfiguration?.options) {
        onOtherShippingOptionsChange(undefined, {
          options: labelConfiguration.options,
        });
      }
    });
  }, [
    configurePackages,
    fulfillmentPlanId,
    onFulfillmentServiceChange,
    onInsuranceProviderChange,
    onOtherShippingOptionsChange,
    onPackagesSave,
    onServiceChange,
    onShipFromChange,
    shouldSyncOnBulkUpdate,
  ]);

  useEffect(() => {
    if (canSyncWithEntities) {
      dispatch(
        Actions.initCswGlobalListener({
          fulfillmentPlanId,
          callback: syncCswWithUpdatedEntity,
          bulkUpdateFinishedHandler,
        }),
      );
    }
    return () => {
      if (canSyncWithEntities) {
        dispatch(Actions.unbindCswGlobalListener({ fulfillmentPlanId }));
      }
    };
  }, [canSyncWithEntities, dispatch, fulfillmentPlanId, syncCswWithUpdatedEntity, bulkUpdateFinishedHandler]);

  // Save prepayDutyAlert to userSettings
  useSaveUserSettingPrepayDutyAlert();

  const applyPresetConfiguration = useApplyPreset(
    fulfillmentPlanId,
    onPresetWithInsuranceProviderApplied,
    onPackagesSave,
    onServiceChange,
    onShipFromChange,
    packages,
    onConfirmationChange,
    otherShippingOptions?.confirmationEnumId?.toString(),
    checkPackagesAfterServiceChange,
    serviceCollectionGuid,
  );

  const updateFPFromRateBrowserConfigureLabel = useCallback(
    ({ modelFromRateBrowser, rateBrowserSelection }) => {
      const { sellerLabelProviderId, rate } = rateBrowserSelection;
      const { serviceId, serviceCollectionGuid: rateServiceCollectionGuid } = rate;
      const { confirmationType } = modelFromRateBrowser;
      onServiceChange({
        serviceId,
        sellerLabelProviderId,
        serviceCollectionGuid: rateServiceCollectionGuid,
        confirmationId: confirmationType,
      });
      updatePackagesFromRateBrowserConfigureLabel({
        modelFromRateBrowser,
        rateBrowserSelection,
      });
    },
    [onServiceChange, updatePackagesFromRateBrowserConfigureLabel],
  );

  const {
    shouldUpdateInsurancePreference,
    setShouldUpdateInsurancePreference,
    handleUpdateInsurancePreference,
    isInsuranceOptionDirty,
    setIsInsuranceOptionDirty,
  } = useUpdateInsurancePreference(insuranceProvider);

  const value = useMemo(
    () => ({
      applyPresetConfiguration,
      availableSellerShippingServices,
      bestWayToShip,
      calculateRecommendedInsuranceValue,
      clearInsuranceValues,
      cswTypeId,
      customs,
      customsReason,
      deliveryTime,
      didLabelCreationFail,
      didLabelCreationSucceed,
      driverTip,
      emailAddress,
      estimatedPickup,
      externalShipFromId,
      fulfillmentPlanId,
      fulfillmentServiceId,
      fulfillmentType,
      hasInsuranceProviderSelected: Number.parseInt(insuranceProvider?.insuranceProviderId ?? 0, 10) > 0,
      insuranceProvider,
      isCreatingLabel,
      isInternational,
      isRating,
      isReturn,
      items,
      labelCreationErrors: createLabelErrors,
      needsCustoms,
      onApplyServiceMappingPackage,
      onConfirmationChange,
      onContentDescriptionChange,
      onCustomsChange,
      onWeightAndDimensionsChange: onWeightAndDimsChange,
      onDriverTipChange,
      onFulfillmentServiceChange,
      onInsuredAmountChange,
      onInsuranceProviderChange,
      onIsReturnChanged,
      onMultiPackageSave,
      onOtherShippingOptionsChange,
      onPackageChange,
      onPackagesSave,
      onPickupChange,
      onSelectedDutyOptionChange,
      onSellerLabelProviderChange,
      onServiceChangeWithSideEffects,
      onShipDateChange,
      onShipFromChange: onShipFromChangeWithSideEffects,
      onShipToChange,
      onShipToEmailChange,
      otherShippingOptions,
      packages,
      pickup,
      rateErrors: visibleRateErrors,
      serviceCollectionGuid,
      serviceIdAndSellerLabelProviderId,
      shipDate,
      shipFromId,
      totalCost,
      triggerRate,
      isTriggerRateDisabled,
      uiRestrictions: restrictions,
      warnings: visibleWarnings,
      availableDutiesOptions,
      shipTo,
      shouldShowDutiesAndTaxes,
      updateFPFromRateBrowserConfigureLabel,
      updatePackagesFromRateBrowserConfigureLabel,
      parentComponentName,
      syncCswWithUpdatedEntity,
      setHasSmartFillDataBeenChanged,
      hasSmartFillDataBeenChanged,
      isSmartFillEnabled,
      smartFillMetaData,
      applyPackageSetFromList,
      shouldUpdateInsurancePreference,
      setShouldUpdateInsurancePreference,
      handleUpdateInsurancePreference,
      isInsuranceOptionDirty,
      setIsInsuranceOptionDirty,
    }),
    [
      applyPresetConfiguration,
      availableSellerShippingServices,
      bestWayToShip,
      calculateRecommendedInsuranceValue,
      clearInsuranceValues,
      createLabelErrors,
      cswTypeId,
      customs,
      customsReason,
      deliveryTime,
      didLabelCreationFail,
      didLabelCreationSucceed,
      driverTip,
      emailAddress,
      estimatedPickup,
      externalShipFromId,
      fulfillmentPlanId,
      fulfillmentServiceId,
      fulfillmentType,
      insuranceProvider,
      isCreatingLabel,
      isInternational,
      isRating,
      isReturn,
      items,
      needsCustoms,
      onApplyServiceMappingPackage,
      onConfirmationChange,
      onContentDescriptionChange,
      onCustomsChange,
      onWeightAndDimsChange,
      onDriverTipChange,
      onFulfillmentServiceChange,
      onInsuredAmountChange,
      onInsuranceProviderChange,
      onIsReturnChanged,
      onMultiPackageSave,
      onOtherShippingOptionsChange,
      onPackageChange,
      onPackagesSave,
      onPickupChange,
      onSelectedDutyOptionChange,
      onSellerLabelProviderChange,
      onServiceChangeWithSideEffects,
      onShipDateChange,
      onShipFromChangeWithSideEffects,
      onShipToChange,
      onShipToEmailChange,
      otherShippingOptions,
      packages,
      pickup,
      visibleRateErrors,
      restrictions,
      serviceCollectionGuid,
      serviceIdAndSellerLabelProviderId,
      shipDate,
      shipFromId,
      shouldShowDutiesAndTaxes,
      totalCost,
      triggerRate,
      isTriggerRateDisabled,
      visibleWarnings,
      availableDutiesOptions,
      shipTo,
      updateFPFromRateBrowserConfigureLabel,
      updatePackagesFromRateBrowserConfigureLabel,
      parentComponentName,
      syncCswWithUpdatedEntity,
      setHasSmartFillDataBeenChanged,
      hasSmartFillDataBeenChanged,
      isSmartFillEnabled,
      smartFillMetaData,
      applyPackageSetFromList,
      shouldUpdateInsurancePreference,
      setShouldUpdateInsurancePreference,
      handleUpdateInsurancePreference,
      isInsuranceOptionDirty,
      setIsInsuranceOptionDirty,
    ],
  );
  return <FulfillmentPlannerContext.Provider value={value}>{children}</FulfillmentPlannerContext.Provider>;
};

FulfillmentPlannerContextProvider.propTypes = {
  availableDutiesOptions: PropTypes.array,
  availableSellerShippingServices: PropTypes.arrayOf(PropTypes.number).isRequired,
  bestWayToShip: BestWayToShipPropType,
  canSyncWithEntities: PropTypes.bool,
  cswTypeId: PropTypes.oneOf([
    'customers-create-label',
    'externalFulfillments',
    'local-delivery',
    'local-delivery-rate',
    'rate-browser',
    'returns',
    'scanToPrint',
    'simple-label-flow',
    'standard',
  ]).isRequired,
  customs: PropTypes.object,
  customsReason: PropTypes.string,
  driverTip: CurrencyPropType,
  emailAddress: PropTypes.string,
  children: PropTypes.node.isRequired,
  estimatedDeliveryTime: PropTypes.string,
  fulfillmentPlanId: PropTypes.string.isRequired,
  fulfillmentServiceId: PropTypes.string,
  initialEstimatedPickup: PropTypes.string,
  // better prop type for this?
  // also the fulfillment plan comes back with a null rate error
  initialRateError: PropTypes.array,
  insuranceProvider: InsuranceProviderPropType.isRequired,
  isInternational: PropTypes.bool,
  isReturn: PropTypes.bool,
  items: PropTypes.array,
  needsCustoms: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  onAvailableSellerShippingServicesChange: PropTypes.func,
  onConfigurationChange: PropTypes.func.isRequired,
  onIsReturnChanged: PropTypes.func,
  onShipToChange: PropTypes.func,
  onShipToEmailChange: PropTypes.func,
  otherShippingOptions: ShippingOptionsPropType,
  packages: PropTypes.arrayOf(PackagePropType).isRequired,
  pickup: PropTypes.string,
  rateTotalCost: CurrencyPropType,
  restrictions: PropTypes.object,
  serviceCollectionGuid: PropTypes.string,
  serviceIdAndSellerLabelProviderId: PropTypes.exact({
    serviceId: PropTypes.string,
    sellerLabelProviderId: PropTypes.string,
  }).isRequired,
  updatedRateWarnings: PropTypes.arrayOf(PropTypes.string),
  shipDate: PropTypes.string,
  shipFromId: PropTypes.string,
  shipTo: addressPropType,
  shouldShowDutiesAndTaxes: PropTypes.bool,
  shouldSyncOnBulkUpdate: PropTypes.bool,
  triggerRate: PropTypes.func.isRequired,
  parentComponentName: PropTypes.string,
  isTriggerRateDisabled: PropTypes.bool,
};

export { FulfillmentPlannerContext, FulfillmentPlannerContextProvider };
